// 翻转大富翁 — 游戏引擎核心逻辑
import type { GameState, Player, FateCard, GlobalEvent, ActiveGlobalEvent } from '../types';
import { STREETS, REVERSE_CARDS, IDENTITIES } from '../data';
import { useGameStore } from '../store/gameStore';
import { addToast } from '../components/Toast/ToastStack';

type StoreType = ReturnType<typeof useGameStore.getState> & {
  addLog: (playerId: string, msg: string, type: string) => void;
  addMoney: (playerId: string, amount: number) => void;
  deductMoney: (playerId: string, amount: number) => number;
  removeReverseCard: (playerId: string, cardId: string) => void;
  addReverseCard: (playerId: string, cardId: string) => void;
  revealIdentity: (playerId: string) => void;
  handleBankruptcy: (playerId: string) => void;
};

// --- 命运卡效果 ---
// ⚠️ 使用 Zustand set() 替代直接 mutation，避免状态不一致导致卡死
export function applyFateCard(
  store: StoreType,
  zustandSet: (partial: any) => void,
  playerId: string,
  card: FateCard
): void {
  const state = store;
  const player = state.players.find(p => p.id === playerId);
  if (!player) return;

  // 守财奴隐藏被动（暴露前）：资产前50%不触发厄运
  if (card.category === 'bad') {
    const identity = IDENTITIES.find(i => i.id === player.identityId);
    if (identity?.id === 'miser') {
      const sortedPlayers = [...state.players].filter(p => !p.bankrupt).sort((a, b) => b.money - a.money);
      const rank = sortedPlayers.findIndex(p => p.id === playerId) + 1;
      if (rank <= sortedPlayers.length / 2) {
        state.addLog(playerId, '守财奴效果：免疫厄运', 'system');
        addToast('🛡️ 守财奴被动：免疫厄运卡', 'info');
        return;
      }
    }
  }

  // 守财奴暴露后免疫：免疫所有负面命运卡
  if (card.category === 'bad' && player.immuneTurns > 0) {
    state.addLog(playerId, `免疫负面效果（剩余${player.immuneTurns}回合）`, 'system');
    addToast(`🛡️ 守财奴免疫：抵御「${card.name}」（剩余${player.immuneTurns}回合）`, 'info');
    return;
  }

  switch (card.id) {
    // === 好运 ===
    case 'fate_carnival':
      state.players.filter(p => !p.bankrupt).forEach(p => {
        state.addMoney(p.id, 200);
      });
      state.addLog('system', '嘉年华：所有玩家获得200金币！', 'event');
      break;

    case 'fate_rainbow':
      if (player.inJail) {
        state.addMoney(playerId, 200);
        zustandSet((s: any) => ({
          players: s.players.map((p: any) =>
            p.id === playerId ? { ...p, inJail: false, jailTurns: 0 } : p
          ),
        }));
        state.addLog(playerId, '彩虹桥：出狱+获得200金币', 'card');
      } else {
        state.addMoney(playerId, 200);
      }
      break;

    case 'fate_windfall':
      state.addMoney(playerId, 300);
      state.addLog(playerId, '天降横财：获得300金币', 'card');
      break;

    case 'fate_treasure': {
      const cardIds = REVERSE_CARDS.map(c => c.id);
      const randomCard = cardIds[Math.floor(Math.random() * cardIds.length)];
      state.addReverseCard(playerId, randomCard);
      state.addLog(playerId, '宝藏发现：获得反转卡！', 'card');
      break;
    }

    // === 厄运 ===
    case 'fate_tornado': {
      if (player.properties.length > 0) {
        const idx = Math.floor(Math.random() * player.properties.length);
        const lostProperty = player.properties[idx];
        const lostCell = state.board[lostProperty];
        zustandSet((s: any) => ({
          players: s.players.map((p: any) =>
            p.id === playerId
              ? { ...p, properties: p.properties.filter((pid: number) => pid !== lostProperty) }
              : p
          ),
          properties: s.properties.filter(
            (p: any) => !(p.cellId === lostProperty && p.ownerId === playerId)
          ),
        }));
        useGameStore.getState().notifyLandChange(lostProperty, 'lost',
          `「${lostCell?.name || '未知地产'}」被龙卷风摧毁`);
        state.addLog(playerId, `龙卷风：失去「${lostCell?.name || '未知地产'}」！`, 'card');
      }
      break;
    }

    case 'fate_lightning': {
      const richest = [...state.players].filter(p => !p.bankrupt).sort((a, b) => b.money - a.money)[0];
      if (richest && richest.properties.length > 0) {
        const mostExpensiveProp = richest.properties
          .map(pid => ({ id: pid, price: state.board[pid]?.price || 0 }))
          .sort((a, b) => b.price - a.price)[0];
        if (mostExpensiveProp) {
          const lostCell = state.board[mostExpensiveProp.id];
          zustandSet((s: any) => ({
            players: s.players.map((p: any) =>
              p.id === richest.id
                ? { ...p, properties: p.properties.filter((pid: number) => pid !== mostExpensiveProp.id) }
                : p
            ),
            properties: s.properties.filter(
              (p: any) => !(p.cellId === mostExpensiveProp.id && p.ownerId === richest.id)
            ),
          }));
          useGameStore.getState().notifyLandChange(mostExpensiveProp.id, 'lost',
            `「${lostCell?.name || '未知地产'}」遭天罚被毁（${richest.name}失去最贵地产）`);
          state.addLog(richest.id, `天罚：失去最贵地产「${lostCell?.name || '未知'}」！`, 'card');
        }
      }
      break;
    }

    case 'fate_pickpocket':
      state.deductMoney(playerId, 200);
      state.addLog(playerId, '扒手光顾：损失200金币', 'card');
      break;

    // === 反转 ===
    case 'fate_wealth_tornado': {
      const activePlayers = state.players.filter(p => !p.bankrupt).sort((a, b) => a.money - b.money);
      if (activePlayers.length >= 2) {
        const poorest = activePlayers[0];
        const richest = activePlayers[activePlayers.length - 1];
        const transfer = Math.round(richest.money * 0.3);
        if (transfer > 0 && poorest.id !== richest.id) {
          zustandSet((s: any) => ({
            players: s.players.map((p: any) => {
              if (p.id === richest.id) return { ...p, money: p.money - transfer };
              if (p.id === poorest.id) return { ...p, money: p.money + transfer };
              return p;
            }),
          }));
          state.addLog('system', `财富龙卷风：${richest.name}→${poorest.name}转移${transfer}金币`, 'card');
        }
      }
      break;
    }

    case 'fate_masquerade': {
      const active = state.players.filter(p => !p.bankrupt);
      if (active.length >= 2) {
        const identities = active.map(p => p.identityId);
        const shifted = [...identities.slice(1), identities[0]];
        zustandSet((s: any) => ({
          players: s.players.map((p: any) => {
            const idx = active.findIndex(a => a.id === p.id);
            if (idx >= 0) return { ...p, identityId: shifted[idx], identityRevealed: false };
            return p;
          }),
        }));
        state.addLog('system', '假面舞会：所有身份牌左移一位！', 'card');
      }
      break;
    }

    case 'fate_thief_king': {
      state.addLog('system', '皇帝轮流做：回合顺序暂时反转', 'card');
      break;
    }
  }
}

// --- 全局事件 ---
const GLOBAL_EVENTS: GlobalEvent[] = [
  {
    id: 'economic_winter',
    name: '经济寒冬',
    emoji: '🌧',
    description: '所有租金减半，地产价格8折',
    duration: 2,
    effect: 'rent_halved_price_80',
  },
  {
    id: 'golden_age',
    name: '黄金时代',
    emoji: '☀️',
    description: '所有租金+50%，地产价格1.2倍',
    duration: 2,
    effect: 'rent_bonus_50_price_120',
  },
  {
    id: 'carnival_event',
    name: '嘉年华',
    emoji: '🎆',
    description: '所有玩家获得150金币',
    duration: 0,
    effect: 'all_players_150',
  },
];

let lastGlobalEventRound = 0;

export function resetGlobalEventTimer() {
  lastGlobalEventRound = 0;
}

/** 按轮次检查是否应触发全局事件（每 2 轮触发一次） */
export function shouldTriggerGlobalEvent(roundNumber: number): boolean {
  const interval = 2; // 每2轮触发一次
  if (roundNumber - lastGlobalEventRound >= interval) {
    lastGlobalEventRound = roundNumber;
    return true;
  }
  return false;
}

/** 强制触发全局事件（事件格踩中时使用，不检查冷却） */
export function forceTriggerGlobalEvent(roundNumber: number): void {
  lastGlobalEventRound = roundNumber;
}

export function getGlobalEvent(): GlobalEvent | null {
  return GLOBAL_EVENTS[Math.floor(Math.random() * GLOBAL_EVENTS.length)] || null;
}

export function applyGlobalEvent(
  store: StoreType,
  event: GlobalEvent
): void {
  switch (event.id) {
    // economic_winter 和 golden_age 的 activeGlobalEvent 由 checkAndTriggerGlobalEvent 通过 Zustand set() 设置
    case 'economic_winter':
    case 'golden_age':
      // 无需在这里设置 activeGlobalEvent，已在 gameStore 中处理
      break;

    case 'carnival_event':
      store.players.filter(p => !p.bankrupt).forEach(p => {
        store.addMoney(p.id, 150);
      });
      store.addLog('system', '嘉年华：所有玩家获得150金币！', 'event');
      break;
  }
}

// --- 反转卡效果 ---
export function applyReverseCard(
  store: StoreType,
  zustandSet: (partial: any) => void,
  playerId: string,
  cardId: string,
  targetId?: string | null,
  extra?: any
): void {
  const state = store;

  switch (cardId) {
    case '乾坤一掷': {
      const player = state.players.find(p => p.id === playerId);
      if (!player) return;
      const bet = Math.max(200, player.money); // 保底200
      const guess = extra?.guess || 3;
      const result = Math.floor(Math.random() * 6) + 1;
      if (result === guess) {
        state.players = state.players.map(p =>
          p.id === playerId ? { ...p, money: p.money + bet } : p
        );
        state.addLog(playerId, `乾坤一掷：猜中！骰子=${result}，赢得${bet}金币`, 'card');
      } else {
        state.players = state.players.map(p =>
          p.id === playerId ? { ...p, money: Math.max(0, p.money - bet) } : p
        );
        state.addLog(playerId, `乾坤一掷：猜错！骰子=${result}，损失${bet}金币`, 'card');
      }
      state.removeReverseCard(playerId, cardId);
      break;
    }

    case '一夜暴富': {
      const roll = Math.floor(Math.random() * 6) + 1;
      let amount = 0;
      if (roll <= 2) amount = 200;
      else if (roll <= 4) amount = 500;
      else amount = 1000;
      state.addMoney(playerId, amount);
      state.addLog(playerId, `一夜暴富：骰子=${roll}，获得${amount}金币！`, 'card');
      state.removeReverseCard(playerId, cardId);
      break;
    }

    case '拆迁令': {
      if (!targetId) return;
      const street = extra?.street;
      if (!street) return;
      const targetedProperties = state.properties.filter(
        p => p.ownerId === targetId &&
        state.board[p.cellId]?.street === street &&
        p.buildingLevel > 0
      );
      targetedProperties.forEach(prop => {
        const compensation = Math.round((state.board[prop.cellId]?.price || 0) * 0.3);
        state.properties = state.properties.map(p =>
          p.cellId === prop.cellId && p.ownerId === targetId
            ? { ...p, buildingLevel: (p.buildingLevel - 1) as 0|1|2|3 }
            : p
        );
        state.addMoney(targetId, compensation);
        const cellName = state.board[prop.cellId]?.name || '未知地产';
        useGameStore.getState().notifyLandChange(prop.cellId, 'downgraded',
          `「${cellName}」${street}建筑被降级`);
      });
      state.addLog(playerId, `拆迁令：${street}的建筑被降级`, 'card');
      state.removeReverseCard(playerId, cardId);
      break;
    }

    case '时光倒流': {
      // 回到上一圈位置
      state._restoreState(playerId);
      state.addLog(playerId, '时光倒流：回到上一圈的位置', 'card');
      state.removeReverseCard(playerId, cardId);
      break;
    }

    case '精准导航': {
      const steps = extra?.steps || 1;
      const result = Math.min(6, Math.max(1, steps));
      // 存储到玩家状态，下回合掷骰时使用
      state.players = state.players.map(p =>
        p.id === playerId ? { ...p, preciseSteps: result } : p
      );
      state.addLog(playerId, `精准导航：下回合将固定走${result}步`, 'card');
      state.removeReverseCard(playerId, cardId);
      break;
    }

    case '强制联盟': {
      if (!targetId || targetId === playerId) return;
      // 先解除双方已有的旧联盟
      const allyB = state.players.find(p => p.id === targetId);
      if (allyB?.allianceWith) {
        // 目标有旧联盟，需要断开
        zustandSet({
          players: state.players.map(p => {
            if (p.id === allyB.allianceWith) return { ...p, allianceWith: null, allianceTurns: 0 };
            return p;
          }),
        });
      }
      const allyA = state.players.find(p => p.id === playerId);
      if (allyA?.allianceWith) {
        zustandSet({
          players: state.players.map(p => {
            if (p.id === allyA.allianceWith) return { ...p, allianceWith: null, allianceTurns: 0 };
            return p;
          }),
        });
      }
      // 使用 Zustand set() 正确更新（不是直接 mutation）
      zustandSet({
        players: state.players.map(p => {
          if (p.id === playerId) return { ...p, allianceWith: targetId, allianceTurns: 2 };
          if (p.id === targetId) return { ...p, allianceWith: playerId, allianceTurns: 2 };
          return p;
        }),
      });
      const targetName = state.players.find(p => p.id === targetId)?.name || '玩家';
      state.addLog(playerId, `强制联盟：与${targetName}结盟2回合`, 'card');
      state.removeReverseCard(playerId, cardId);
      break;
    }
  }
}
